import { isValidSlug } from "./isValidSlug"

/**
 * Query param used to carry the page a visitor should be sent back to once
 * they finish an interstitial flow (e.g. the contact form).
 */
export const RETURN_PATH_PARAM = "returnTo"

interface GetInternalReturnPathArgs {
  value?: string | string[] | null
  market: string
}

/**
 * Validates a caller-supplied return path before it is used as a link target.
 *
 * The value arrives from a query param, so it is untrusted: an absolute URL or a
 * protocol-relative value like `//attacker.example` would escape the origin
 * entirely (CWE-601). The pattern is an allowlist: plain segments under the
 * market the current page renders under, so traversal is rejected and a
 * `/de/…` destination cannot be smuggled into a `/us/…` page. `isValidSlug`
 * earns its place twice: it rejects a malformed market, and it is what makes
 * interpolating `market` into the pattern safe.
 *
 * @param value - Raw query param value; arrays (repeated params) are rejected
 * @param market - Market segment the current page renders under
 * @returns The path, or `null` when it cannot be trusted
 */
export const getInternalReturnPath = ({
  value,
  market,
}: GetInternalReturnPathArgs): string | null =>
  typeof value === "string" &&
  isValidSlug(market) &&
  new RegExp(`^/${market}(?:/[a-zA-Z0-9_-]+)*/?$`).test(value)
    ? value
    : null

interface WithReturnPathArgs {
  href: string
  returnPath?: string | null
}

/**
 * Appends the current path to a link so the destination knows where to send the
 * visitor back to. The value is only a hint: the destination validates it with
 * {@link getInternalReturnPath} before using it.
 *
 * The href is split rather than concatenated because callers pass URLs they did
 * not build: the checkout footer's come from the backend and may already carry a
 * query string, and mirror-anchor's footer passes absolute ones. So an existing
 * `returnTo` is replaced rather than duplicated (a repeated param arrives as a
 * `string[]` and is rejected), and a `#fragment` stays behind the query.
 *
 * @param href - Link target to decorate
 * @param returnPath - Path to return to, typically `usePathname()`
 * @returns The decorated href, or `href` untouched when there is nothing worth
 * carrying
 */
export const withReturnPath = ({
  href,
  returnPath,
}: WithReturnPathArgs): string => {
  if (!returnPath || returnPath === "/") {
    return href
  }

  const hashIndex = href.indexOf("#")
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex)
  const withoutHash = hashIndex === -1 ? href : href.slice(0, hashIndex)

  const queryIndex = withoutHash.indexOf("?")
  const path =
    queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex)
  const params = new URLSearchParams(
    queryIndex === -1 ? "" : withoutHash.slice(queryIndex + 1)
  )

  params.set(RETURN_PATH_PARAM, returnPath)

  return `${path}?${params.toString()}${hash}`
}
